import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import type { ServerACLResource } from '@/api/type-server'
import { Button } from '@/components/base/Button'
import { Divider } from '@/components/base/Divider'
import { Loading } from '@/components/base/Loading'
import { message } from '@/components/base/Message'
import { Modal } from '@/components/base/Modal'
import { EmptyView } from '@/components/EmptyView'
import { Trans } from '@/components/Trans'
import { ACLDirection } from '@/consts/acl'
import { getEIPACLPath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { PurchasedEIP } from '@/models/eip'
import { formatDatePreset } from '@/utils/formatter'

interface ACLContentProps {
  eip: PurchasedEIP
}

// type: 0=Invalid, 1=PublicIP
const ACL_TYPE_KEY: Record<number, string> = {
  0: 'detail.aclTypeInvalid',
  1: 'detail.aclTypePublicIP',
}

// status: 0=Invalid, 1=Running, 2=Inactive
const ACL_STATUS_META: Record<number, { key: string; cls: string }> = {
  0: { key: 'detail.aclStatusInvalid', cls: 'text-tertiary' },
  1: { key: 'detail.aclStatusRunning', cls: 'text-active' },
  2: { key: 'detail.aclStatusInactive', cls: 'text-tertiary' },
}

const ACL_RESOURCE_NOT_EXISTS_CODE = 224

export function ACLContent({ eip }: ACLContentProps) {
  const { t } = useTranslation('eip')
  const navigate = useNavigate()
  const ctx = useUnifiedContext()
  const queryClient = useQueryClient()
  const [destroyModalOpen, setDestroyModalOpen] = useState(false)
  const aclPath = getEIPACLPath(ctx, eip.resID)

  const {
    data: acl,
    isLoading: aclLoading,
    isError: aclError,
  } = useQuery({
    queryKey: ['eipACL', ctx.mode, ctx.teamResID, ctx.memberResID, eip.resID],
    retry: false,
    queryFn: async ({ signal }) => {
      const res = await API.getACLResource(
        { net_res_id: eip.resID },
        { signal, errMsg: { [ACL_RESOURCE_NOT_EXISTS_CODE]: '' } },
      )
      if (res.code === ACL_RESOURCE_NOT_EXISTS_CODE) return null
      if (res.code !== 0) throw new Error(res.msg)
      return res.resource
    },
  })

  const createACLResource = useMutation({
    mutationFn: async () => {
      const res = await API.createACLResource({ net_res_id: eip.resID })
      if (res.code !== 0) throw new Error(res.msg)
      return res.resource
    },
    onSuccess: () => {
      message.success(t('common.message.success', { ns: 'translation' }))
      queryClient.invalidateQueries({
        queryKey: ['eipACL', ctx.mode, ctx.teamResID, ctx.memberResID, eip.resID],
      })
    },
  })

  const destroyACLResource = useMutation({
    mutationFn: async () => {
      const res = await API.deleteACLResource({ net_res_id: eip.resID })
      if (res.code !== 0) throw new Error(res.msg)
    },
    onSuccess: () => {
      message.success(t('common.message.success', { ns: 'translation' }))
      setDestroyModalOpen(false)
      queryClient.setQueryData(
        ['eipACL', ctx.mode, ctx.teamResID, ctx.memberResID, eip.resID],
        null,
      )
      if (acl?.resID) {
        queryClient.removeQueries({
          queryKey: ['eipACLRulesCount', ctx.mode, ctx.teamResID, ctx.memberResID, acl.resID],
        })
      }
    },
  })

  const { data: rulesCount = 0 } = useQuery({
    queryKey: ['eipACLRulesCount', ctx.mode, ctx.teamResID, ctx.memberResID, acl?.resID],
    enabled: !!acl?.resID,
    queryFn: async ({ signal }) => {
      const [inb, outb] = await Promise.all([
        API.getACLL3Rules(
          {
            acl_res_id: acl!.resID,
            traffic_direction: ACLDirection.Inbound,
            page: 1,
            per_page: 1,
          },
          { signal },
        ),
        API.getACLL3Rules(
          {
            acl_res_id: acl!.resID,
            traffic_direction: ACLDirection.Outbound,
            page: 1,
            per_page: 1,
          },
          { signal },
        ),
      ])
      if (inb.code !== 0) throw new Error(inb.msg)
      if (outb.code !== 0) throw new Error(outb.msg)
      return (inb.pagination?.total ?? 0) + (outb.pagination?.total ?? 0)
    },
  })

  if (aclLoading) {
    return (
      <div className="bg-neutral-50 rounded-lg p-8 flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (aclError) {
    return (
      <div className="bg-neutral-50 rounded-lg p-4 t-sm text-tertiary">
        <Trans ns="eip" i18nKey="detail.aclEmpty">
          No ACL resource available.
        </Trans>
      </div>
    )
  }

  if (!acl) {
    return (
      <div className="bg-neutral-50 rounded-lg min-h-[220px]">
        <EmptyView
          imageSize="small"
          title={<span className="t-sm text-secondary">{t('detail.aclEmptyTitle')}</span>}
          action={
            <Button
              type="secondary"
              size="sm"
              disabled={createACLResource.isPending}
              onClick={() => createACLResource.mutate()}
            >
              {t('detail.aclAddResource')}
            </Button>
          }
        />
      </div>
    )
  }

  const statusMeta = ACL_STATUS_META[acl.status] ?? ACL_STATUS_META[0]
  const typeKey = ACL_TYPE_KEY[acl.type] ?? ACL_TYPE_KEY[0]

  return (
    <div className="bg-neutral-50 rounded-lg p-4 flex flex-col gap-4">
      <div className="grid grid-cols-5 gap-4">
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.aclID">
              ACL ID
            </Trans>
          </div>
          <div className="t-sb text-primary">{acl.resID}</div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.aclName">
              Name
            </Trans>
          </div>
          <div className="t-sb text-primary">{acl.name || '-'}</div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.aclStatus">
              ACL Status
            </Trans>
          </div>
          <div className={`t-sb ${statusMeta.cls}`}>{t(statusMeta.key)}</div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.aclType">
              ACL Type
            </Trans>
          </div>
          <div className="t-sb text-primary">{t(typeKey)}</div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.aclUpdateTime">
              Update Time
            </Trans>
          </div>
          <div className="t-sb text-primary">
            {acl.updatedAt?.seconds
              ? formatDatePreset(acl.updatedAt.seconds * 1000, 'minute')
              : '-'}
          </div>
        </div>
      </div>

      <div>
        <div className="t-xs text-secondary mb-1">
          <Trans ns="eip" i18nKey="detail.aclNotes">
            Notes/Description
          </Trans>
        </div>
        <div className="t-sb text-primary">-</div>
      </div>

      <div>
        <Divider className="mb-3 border-neutral-200" />
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="text-highlight cursor-pointer t-x-sb"
            onClick={(e) => {
              e.stopPropagation()
              navigate(aclPath)
            }}
          >
            {t('detail.aclManageRules', { count: rulesCount })}
          </button>
          <button
            type="button"
            className="text-error cursor-pointer t-x-sb disabled:cursor-not-allowed disabled:opacity-50"
            disabled={destroyACLResource.isPending}
            onClick={(e) => {
              e.stopPropagation()
              setDestroyModalOpen(true)
            }}
          >
            {t('detail.aclDestroyResource')}
          </button>
        </div>
      </div>

      {destroyModalOpen && (
        <ACLResourceDestroyModal
          acl={acl}
          rulesCount={rulesCount}
          statusClassName={statusMeta.cls}
          statusLabel={t(statusMeta.key)}
          loading={destroyACLResource.isPending}
          onClose={() => setDestroyModalOpen(false)}
          onConfirm={() => destroyACLResource.mutate()}
        />
      )}
    </div>
  )
}

interface ACLResourceDestroyModalProps {
  acl: ServerACLResource
  rulesCount: number
  statusClassName: string
  statusLabel: string
  loading: boolean
  onClose: () => void
  onConfirm: () => void
}

function ACLResourceDestroyModal({
  acl,
  rulesCount,
  statusClassName,
  statusLabel,
  loading,
  onClose,
  onConfirm,
}: ACLResourceDestroyModalProps) {
  const { t } = useTranslation('eip')

  const fields = [
    { label: t('detail.aclID'), value: acl.resID },
    { label: t('detail.aclName'), value: acl.name || '-' },
    { label: t('detail.aclStatus'), value: statusLabel, className: statusClassName },
    { label: t('detail.aclDestroyRulesIncluded'), value: rulesCount },
  ]

  return (
    <Modal
      className="w-[640px]"
      open={true}
      onClose={() => {
        if (!loading) onClose()
      }}
    >
      <Modal.Header>
        <Trans ns="eip" i18nKey="detail.aclDestroyTitle">
          Destroy ACL Resource
        </Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div>
            <Trans
              ns="eip"
              i18nKey="detail.aclDestroyLead"
              components={{ red: <span className="text-error" /> }}
            >
              {'This action will '}
              <span className="text-error">permanently destroy</span>
              {' this ACL resource and all rules in it. This operation is '}
              <span className="text-error">irreversible</span>
              {'.'}
            </Trans>
          </div>

          <div className="bg-neutral-50 rounded-lg p-4 grid grid-cols-4 gap-4">
            {fields.map((field) => (
              <div key={field.label} className="min-w-0">
                <div className="t-xs text-secondary mb-1">{field.label}</div>
                <div className={`t-sb truncate ${field.className ?? 'text-primary'}`}>
                  {field.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            if (!loading) onClose()
          }}
          className="min-w-[88px]"
        >
          <Trans ns="eip" i18nKey="aclRules.cancel">
            Cancel
          </Trans>
        </Button>
        <Button type="danger" disabled={loading} onClick={onConfirm} className="min-w-[108px]">
          <Trans ns="eip" i18nKey="detail.aclDestroyConfirm">
            Destroy ACL
          </Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
